import { useSiteMetadata } from "hooks/use-site-metadata";
import previewImage from "images/main/preview.png";
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

interface HeadProps {
    title?: string;
    description?: string;
    image?: { path: string };
    path?: string;
    canonicalURL?: string;
    slug?: string;
    language?: string;
    pageType?: "blog" | "article";
    datePublished?: string;
    dateModified?: string;
    author?: string;
    faqSchema?: Array<{question: string; answer: string}>;
    breadcrumbSchema?: Array<{name: string; url: string}>;
}

const Head: React.FC<HeadProps> = ({
    title,
    description,
    image,
    path,
    canonicalURL,
    slug,
    language,
    pageType,
    datePublished,
    dateModified,
    author,
    faqSchema,
    breadcrumbSchema,
}) => {
    const siteMetadata = useSiteMetadata();

    const { t } = useTranslation();

    const titleOrDefault = title ?? t("EP_INDEX_SEO_TITLE");
    const descriptionOrDefault = description ?? t("EP_INDEX_SEO_DESCRIPTION");
    const keywords: string = t("EP_INDEX_SEO_KEYWORDS");

    const slugForImage = slug?.split("/").filter(Boolean).pop();
    const imageUrlOrDefault = `${siteMetadata.siteUrl}${
        image?.path ||
        (slugForImage ? `/og-images/${slugForImage}.png` : previewImage)
    }`;

    const isRoot = path == "/" || path == "*";

    // Note: CSP
    //
    // /* We load fonts, images etc from inline data and blobs, so enable them all as the default */
    // default-src 'self' blob: data: https://*.ente.io;
    // style-src 'self' 'unsafe-inline';
    // /* Gatsby currently needs 'unsafe-eval' and 'unsafe-inline' */
    // script-src 'self' 'unsafe-eval' 'unsafe-inline' https://*.ente.io;
    // /* Allow connections to api.github.com for the download page */
    // connect-src 'self' https://*.ente.io https://*.github.com/;
    //
    // /* Lock down some things that we don't need */
    // base-uri 'self';
    // form-action 'none';
    // object-src 'none';
    // block-all-mixed-content;

    return (
        <Helmet
            htmlAttributes={{
                lang: language || "en",
            }}
        >
            <meta charSet="utf-8" />
            <title>{titleOrDefault}</title>
            <meta name="description" content={descriptionOrDefault} />
            <meta name="keywords" content={keywords} />

            <meta name="msapplication-TileColor" content="#da532c" />

            <meta property="og:site_name" content="ente" />
            <meta property="og:type" content={pageType === "blog" || pageType === "article" ? "article" : "website"} />
            <meta property="og:title" content={titleOrDefault} />
            <meta property="og:description" content={descriptionOrDefault} />
            <meta property="og:image" content={imageUrlOrDefault} />
            <meta property="og:url" content={canonicalURL || `${siteMetadata.siteUrl}${path === '*' || !path ? '' : path}`} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={titleOrDefault} />
            <meta name="twitter:description" content={descriptionOrDefault} />
            <meta name="twitter:image" content={imageUrlOrDefault} />
            <meta name="twitter:site" content="@enteio" />

            {canonicalURL && <link rel="canonical" href={canonicalURL}></link>}

            {/* hreflang tags are handled by gatsby-ssr.js for translatable pages */}

            {/* See above - Note: CSP */}
            <meta
                http-equiv="Content-Security-Policy"
                content="
                    default-src 'self' blob: data: https://*.ente.io https://*.ente-website.pages.dev https://*.posthog.com;
                    style-src 'self' 'unsafe-inline' https://*.posthog.com https://ph.ente.io;
                    script-src 'self' 'unsafe-eval' 'unsafe-inline' https://*.ente.io https://*.ente-website.pages.dev https://*.posthog.com https://scripts.simpleanalyticscdn.com;
                    connect-src 'self' https://*.ente.io https://*.ente-website.pages.dev https://*.github.com/ https://*.posthog.com https://queue.simpleanalyticscdn.com;
                    img-src 'self' blob: data: https://*.posthog.com https://queue.simpleanalyticscdn.com https://simpleanalyticsbadges.com https://*.ente.io;
                    font-src 'self' data: https://*.posthog.com https://*.ente-website.pages.dev https://*.ente.io;
                    media-src 'self' https://*.posthog.com https://*.ente-website.pages.dev https://*.ente.io;
                    frame-src 'self' https://*.cloudflarestream.com https://www.youtube.com https://embed.ente.io;
                    base-uri 'self';
                    form-action 'none';
                    object-src 'self' blob: data:;
                    block-all-mixed-content;
                "
            />

            {isRoot && (
                <script type="application/ld+json">
                        {`
                        {
                            "@context": "https://schema.org",
                            "@type": "Organization",
                        "@id": "http://ente.io/#organization",
                        "url": "https://ente.io",
                        "name": "ente",
                        "legalName": "Ente Technologies, Inc.",
                        "description": "` +
                        descriptionOrDefault +
                        `",
                        "logo": "https://ente.io/assets/logo.png",
                        "address": {
                            "@type": "PostalAddress",
                            "name": "Ente Technologies, Inc.",
                            "addressCountry": "USA",
                            "addressLocality": "Dover",
                            "addressRegion": "Delaware",
                            "streetAddress": "1111B S Governors Ave #6032",
                            "postalCode": "19904"
                        },
                        "email": "support@ente.io",
                        "foundingDate": "2020-02-01",
                        "sameAs": [
                            "https://ente.io/#corporation",
                            "https://twitter.com/enteio",
                            "https://github.com/ente-io",
                            "https://reddit.com/r/ente-io",
                            "https://www.crunchbase.com/organization/ente-technologies",
                            "https://alternativeto.net/software/ente/about/",
                            "https://www.youtube.com/@entestudio",
                            "https://angel.co/company/ente",
                            "https://medium.com/@enteio",
                            "https://news.ycombinator.com/item?id=28347439"
                        ],
                        "owns": [
                            {
                                "@context": "http://schema.org",
                                "@id": "https://ente.io/#mobile",
                                "@type": "MobileApplication",
                                "name": "ente",
                                "description": "` +
                        descriptionOrDefault +
                        `",
                                "applicationCategory": "UtilitiesApplication",
                                "url": "https://ente.io/download",
                                "downloadURL": "https://ente.io/download",
                                "installURL": "https://ente.io/download",
                                "featureList": "End-to-end Encryption, Encrypted Photos, Secure Photo Storage, Encrypted Cloud Photo Storage, Encrypted Storage, Zero-Knowledge Cloud Storage, Family Plans, Album Sharing, Collaboration, Link Sharing, Locked Photos, Privacy, Security, Safety, Encryption",
                                "about": {
                                    "description": "` +
                        descriptionOrDefault +
                        `"
                                },
                                "operatingSystem": "All",
                                "aggregateRating": {
                                    "@type": "AggregateRating",
                                    "ratingValue": "4.7",
                                    "ratingCount": "238"
                                },
                                "offers": {
                                    "@type": "AggregateOffer",
                                    "highPrice": "19.99",
                                    "lowPrice": "0.99",
                                    "offerCount": "4",
                                    "priceCurrency": "EUR",
                                    "offers": [
                                        {
                                            "@type": "Offer",
                                            "url": "https://ente.io/#pricing",
                                            "price": "0.99",
                                            "priceCurrency": "EUR"
                                        },
                                        {
                                            "@type": "Offer",
                                            "url": "https://ente.io/#pricing",
                                            "price": "4.99",
                                            "priceCurrency": "EUR"
                                        },
                                        {
                                            "@type": "Offer",
                                            "url": "https://ente.io/#pricing",
                                            "price": "9.99",
                                            "priceCurrency": "EUR"
                                        },
                                        {
                                            "@type": "Offer",
                                            "url": "https://ente.io/#pricing",
                                            "price": "19.99",
                                            "priceCurrency": "EUR"
                                        }
                                    ]
                                },
                                "publisher": {
                                    "@context": "http://schema.org/",
                                    "@id": "https://ente.io/#corporation",
                                    "@type": "Corporation",
                                    "name": "ente",
                                    "url": "https://ente.io",
                                    "logo": "https://ente.io/assets/logo.png"
                                }
                            }
                        ]
                    }
                `}
                </script>
            )}

            {/* WebSite schema with SearchAction - Commented out until blog search is implemented
            <script type="application/ld+json">
                {`
                {
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "Ente Photos",
                    "url": "https://ente.io",
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": {
                            "@type": "EntryPoint",
                            "urlTemplate": "https://ente.io/blog/?s={search_term_string}"
                        },
                        "query-input": "required name=search_term_string"
                    }
                }
            `}
            </script>
            */}

            {pageType === "blog" && datePublished && (
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": "${titleOrDefault}",
                        "description": "${descriptionOrDefault}",
                        "image": "${imageUrlOrDefault}",
                        "datePublished": "${datePublished}",
                        "dateModified": "${dateModified || datePublished}",
                        "author": {
                            "@type": "Person",
                            "name": "${author || "Ente Team"}"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "@id": "http://ente.io/#organization",
                            "name": "ente",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://ente.io/assets/logo.png"
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": "${siteMetadata.siteUrl}${path || ""}"
                        }
                    }
                `}
                </script>
            )}

            {pageType === "article" && (
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": "${titleOrDefault}",
                        "description": "${descriptionOrDefault}",
                        "image": "${imageUrlOrDefault}",
                        "author": {
                            "@type": "Organization",
                            "name": "ente"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "@id": "http://ente.io/#organization",
                            "name": "ente",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://ente.io/assets/logo.png"
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": "${siteMetadata.siteUrl}${path || ""}"
                        }
                    }
                `}
                </script>
            )}

            {breadcrumbSchema && breadcrumbSchema.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": breadcrumbSchema.map((item, idx) => ({
                            "@type": "ListItem",
                            "position": idx + 1,
                            "name": item.name,
                            "item": item.url
                        }))
                    })}
                </script>
            )}

            {faqSchema && faqSchema.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqSchema.map((faq) => ({
                            "@type": "Question",
                            "name": faq.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": faq.answer
                            }
                        }))
                    })}
                </script>
            )}
        </Helmet>
    );
};

export default Head;
